import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';

const NextStepsTab = ({ ein, planNumber, planYear, planDetails }) => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    // Personal Information
    name: '',
    email: '',
    company: '',
    phone: '',
    city: '',
    state: '',
    zipCode: '',
    
    // User Type
    userType: 'participant', // 'participant' or 'sponsor'
    worksInHR: null,
    sponsorRole: '',
    numEmployees: '',
    planAssets: '',
    
    // Current Situation
    currentProvider: '',
    renewalDate: '',
    timeline: '',
    painPoints: [],
    otherPainPoint: '',
    
    // Provider Recommendations
    wantsRecommendations: true,
    selectedProviders: ['all'],
    wantsConsultation: false,
    preferredContact: 'email',
    bestTimeToCall: '',
    
    // Consent
    consentContact: false,
    consentSharing: false,
    consentTerms: false,
    consentDisclosure: false,
    
    // Additional
    additionalComments: '',
  });
  
  // Enhanced upload state for multiple document types
  const [uploadedDocuments, setUploadedDocuments] = useState({
    spd: [],
    bpd: [],
    adoption_agreement: []
  });
  const [extractedData, setExtractedData] = useState({
    spd: null,
    bpd: null,
    adoption_agreement: null
  });
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [currentExtracting, setCurrentExtracting] = useState(null);
  
  // UI state
  const [errors, setErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Providers list (these would be dynamically generated based on comparison data)
  const recommendedProviders = [
    { id: 'provider_a', name: 'Vanguard', estimatedSavings: 45000 },
    { id: 'provider_b', name: 'Fidelity', estimatedSavings: 42000 },
    { id: 'provider_c', name: 'Schwab', estimatedSavings: 40000 },
  ];

  const currentProviders = [
    'Fidelity', 'Vanguard', 'Schwab', 'T. Rowe Price', 'TIAA',
    'Principal', 'Empower', 'ADP', 'Paychex', 'John Hancock',
    'MassMutual', 'Nationwide', 'Transamerica', 'Other'
  ];

  const painPointOptions = [
    { id: 'high_fees', label: 'High administrative fees' },
    { id: 'poor_returns', label: 'Poor investment returns' },
    { id: 'limited_options', label: 'Limited investment options' },
    { id: 'poor_service', label: 'Poor participant service' },
    { id: 'compliance', label: 'Compliance concerns' },
    { id: 'recordkeeping', label: 'Difficult recordkeeping' },
    { id: 'other', label: 'Other' },
  ];

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle pain points (multi-select)
  const handlePainPointToggle = (pointId) => {
    setFormData(prev => {
      const painPoints = prev.painPoints.includes(pointId)
        ? prev.painPoints.filter(p => p !== pointId)
        : [...prev.painPoints, pointId];
      return { ...prev, painPoints };
    });
  };

  // Handle provider selection
  const handleProviderToggle = (providerId) => {
    setFormData(prev => {
      if (providerId === 'all') {
        return { ...prev, selectedProviders: ['all'] };
      }
      
      let selected = prev.selectedProviders.filter(p => p !== 'all');
      if (selected.includes(providerId)) {
        selected = selected.filter(p => p !== providerId);
      } else {
        selected.push(providerId);
      }
      
      // If all individual providers selected, switch to 'all'
      if (selected.length === recommendedProviders.length) {
        selected = ['all'];
      }
      
      return { ...prev, selectedProviders: selected };
    });
  };

  // Enhanced file upload handler for multiple document types
  const handleFileUpload = async (event, documentType) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    setUploading(true);
    
    try {
      // Validate files
      const validFiles = files.filter(file => {
        const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
        const isValidType = file.type === 'application/pdf';
        return isValidSize && isValidType;
      });
      
      if (validFiles.length !== files.length) {
        alert('Some files were rejected. Only PDF files under 10MB are allowed.');
      }
      
      if (validFiles.length === 0) {
        setUploading(false);
        return;
      }
      
      // Process each file
      for (const file of validFiles) {
        // Add to uploaded files list
        const fileInfo = {
          id: Math.random().toString(36),
          file: file,
          name: file.name,
          size: file.size,
          type: documentType,
          uploadedAt: new Date(),
        };
        
        setUploadedDocuments(prev => ({
          ...prev,
          [documentType]: [...prev[documentType], fileInfo]
        }));
        
        // Extract data based on document type
        await extractDocumentData(fileInfo, documentType);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Extract data from uploaded document
  const extractDocumentData = async (fileInfo, documentType) => {
    setExtracting(true);
    setCurrentExtracting(`${documentType.toUpperCase()}: ${fileInfo.name}`);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', fileInfo.file);
      
      // Determine which endpoint to call
      const endpointMap = {
        'spd': '/extract-spd',
        'bpd': '/extract-bpd',
        'adoption_agreement': '/extract-adoption-agreement'
      };
      
      const endpoint = endpointMap[documentType];
      
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (data.success && data.extracted_data) {
        setExtractedData(prev => ({
          ...prev,
          [documentType]: data.extracted_data
        }));
        
        // Auto-fill form fields if this is SPD or AA
        if (documentType === 'adoption_agreement' || documentType === 'spd') {
          const extractedInfo = data.extracted_data;
          
          if (extractedInfo.employer_name || extractedInfo.employer_name_legal) {
            const employerName = extractedInfo.employer_name || extractedInfo.employer_name_legal;
            if (!formData.company) {
              handleInputChange('company', employerName);
            }
          }
        }
        
        alert(`${documentType.toUpperCase()} extracted successfully!`);
      } else {
        alert(`Extraction failed: ${data.error || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error('Extraction error:', error);
      alert(`Error extracting ${documentType}. Please try again.`);
    } finally {
      setExtracting(false);
      setCurrentExtracting(null);
    }
  };

  // Remove uploaded document
  const removeDocument = (documentType, fileId) => {
    setUploadedDocuments(prev => ({
      ...prev,
      [documentType]: prev[documentType].filter(f => f.id !== fileId)
    }));
    
    // Clear extracted data if all documents of this type are removed
    if (uploadedDocuments[documentType].length === 1) {
      setExtractedData(prev => ({
        ...prev,
        [documentType]: null
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    // Sponsor-specific required fields
    if (formData.userType === 'sponsor') {
      if (!formData.sponsorRole) newErrors.sponsorRole = 'Role is required for plan sponsors';
      if (!formData.numEmployees) newErrors.numEmployees = 'Number of employees is required';
    }
    
    // Timeline required if wants recommendations
    if (formData.wantsRecommendations && !formData.timeline) {
      newErrors.timeline = 'Please select a timeline';
    }
    
    // Current provider required
    if (!formData.currentProvider) {
      newErrors.currentProvider = 'Please select your current provider';
    }
    
    // Consent checkboxes
    if (!formData.consentContact) newErrors.consentContact = 'Required';
    if (!formData.consentSharing) newErrors.consentSharing = 'Required';
    if (!formData.consentTerms) newErrors.consentTerms = 'Required';
    if (!formData.consentDisclosure) newErrors.consentDisclosure = 'Required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate PDF report
  const handleDownloadPDF = async () => {
    try {
      // Call backend to generate PDF
      const response = await fetch(`${API_BASE_URL}/generate-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ein,
          plan_number: planNumber,
          plan_year: parseInt(planYear),
          include_name: formData.name ? true : false,
          user_name: formData.name || null,
          user_company: formData.company || null,
        })
      });
      
      if (!response.ok) {
        throw new Error('PDF generation failed');
      }
      
      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plan-analysis-${ein}-${planNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert('Please fix the errors in the form');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Prepare submission data
      const submissionData = {
        ...formData,
        ein,
        plan_number: planNumber,
        plan_year: parseInt(planYear),
        uploaded_documents: {
          spd: uploadedDocuments.spd.map(f => ({ name: f.name, size: f.size, uploadedAt: f.uploadedAt })),
          bpd: uploadedDocuments.bpd.map(f => ({ name: f.name, size: f.size, uploadedAt: f.uploadedAt })),
          adoption_agreement: uploadedDocuments.adoption_agreement.map(f => ({ name: f.name, size: f.size, uploadedAt: f.uploadedAt }))
        },
        extracted_data: extractedData,
        submitted_at: new Date().toISOString(),
      };
      
      // Send to backend
      const response = await fetch(`${API_BASE_URL}/submit-next-steps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Submission failed');
      }
      
      setSubmitSuccess(true);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Submit error:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Success message
  if (submitSuccess) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Thank You! Your Request Has Been Submitted
          </h2>
          
          <p className="text-lg text-neutral-700 mb-8">
            Here's what happens next:
          </p>
          
          <div className="text-left max-w-2xl mx-auto space-y-4 mb-8">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-teal rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <h4 className="font-bold text-neutral-900">Immediate Confirmation</h4>
                <p className="text-neutral-600">Check your email for a confirmation and your personalized report</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-teal rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <h4 className="font-bold text-neutral-900">Document Review (24 hours)</h4>
                <p className="text-neutral-600">We'll analyze your uploaded documents and extract key plan details</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-teal rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <h4 className="font-bold text-neutral-900">Provider Recommendations (2 business days)</h4>
                <p className="text-neutral-600">Receive detailed quotes from your selected providers</p>
              </div>
            </div>
            
            {formData.wantsConsultation && (
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-teal rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-bold text-neutral-900">Consultation Call</h4>
                  <p className="text-neutral-600">We'll reach out to schedule your consultation</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-4 justify-center">
            <button
              onClick={handleDownloadPDF}
              className="px-8 py-3 bg-teal text-white rounded-xl hover:bg-teal-dark transition-all font-semibold"
            >
              Download Your Report
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-neutral-200 text-neutral-900 rounded-xl hover:bg-neutral-300 transition-all font-semibold"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="max-w-5xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* Section 1: Get Your Summary Report */}
        <div className="bg-white border-2 border-neutral-200 rounded-2xl p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-neutral-900">Get Your Summary Report</h3>
              <p className="text-neutral-600">Your personalized cost analysis and recommendations</p>
            </div>
          </div>
          
          <p className="text-neutral-700 mb-6">
            Download a comprehensive PDF report showing how much you could save by switching to a lower-cost provider. 
            This report includes your historical analysis, S&P 500 comparison, and detailed savings projections.
          </p>
          
          <button
            type="button"
            onClick={handleDownloadPDF}
            className="w-full md:w-auto px-8 py-3 bg-teal text-white rounded-xl hover:bg-teal-dark transition-all font-semibold text-lg shadow-soft"
          >
            📄 Download PDF Report
          </button>
        </div>

        {/* Section 2: Your Information */}
        <div className="bg-white border-2 border-neutral-200 rounded-2xl p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-neutral-900">Your Information</h3>
              <p className="text-neutral-600">Help us understand your situation</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-4 py-3 border-2 ${errors.name ? 'border-red-500' : 'border-neutral-200'} rounded-xl focus:outline-none focus:border-teal transition-colors`}
                placeholder="John Smith"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
            
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-4 py-3 border-2 ${errors.email ? 'border-red-500' : 'border-neutral-200'} rounded-xl focus:outline-none focus:border-teal transition-colors`}
                placeholder="john@company.com"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>
            
            {/* Company */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-teal transition-colors"
                placeholder="Acme Corp"
              />
            </div>
            
            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-teal transition-colors"
                placeholder="(555) 123-4567"
              />
            </div>
            
            {/* City */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                City
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-teal transition-colors"
                placeholder="San Francisco"
              />
            </div>
            
            {/* State & Zip */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-teal transition-colors"
                  placeholder="CA"
                  maxLength="2"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  Zip
                </label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-teal transition-colors"
                  placeholder="94102"
                  maxLength="5"
                />
              </div>
            </div>
          </div>
          
          {/* User Type */}
          <div className="mt-8">
            <label className="block text-sm font-semibold text-neutral-700 mb-3">
              I am a: <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex-1 relative">
                <input
                  type="radio"
                  name="userType"
                  value="participant"
                  checked={formData.userType === 'participant'}
                  onChange={(e) => handleInputChange('userType', e.target.value)}
                  className="peer sr-only"
                />
                <div className="px-6 py-4 border-2 border-neutral-200 rounded-xl cursor-pointer transition-all peer-checked:border-teal peer-checked:bg-teal/5">
                  <div className="font-semibold text-neutral-900">Plan Participant</div>
                  <div className="text-sm text-neutral-600">Employee with a 401(k)</div>
                </div>
              </label>
              
              <label className="flex-1 relative">
                <input
                  type="radio"
                  name="userType"
                  value="sponsor"
                  checked={formData.userType === 'sponsor'}
                  onChange={(e) => handleInputChange('userType', e.target.value)}
                  className="peer sr-only"
                />
                <div className="px-6 py-4 border-2 border-neutral-200 rounded-xl cursor-pointer transition-all peer-checked:border-teal peer-checked:bg-teal/5">
                  <div className="font-semibold text-neutral-900">Plan Sponsor</div>
                  <div className="text-sm text-neutral-600">Decision maker / HR</div>
                </div>
              </label>
            </div>
          </div>
          
          {/* Conditional fields for Participant */}
          {formData.userType === 'participant' && (
            <div className="mt-6">
              <label className="block text-sm font-semibold text-neutral-700 mb-3">
                Do you work in HR or Benefits?
              </label>
              <div className="flex gap-4">
                <label className="relative">
                  <input
                    type="radio"
                    name="worksInHR"
                    value="yes"
                    checked={formData.worksInHR === true}
                    onChange={() => handleInputChange('worksInHR', true)}
                    className="peer sr-only"
                  />
                  <div className="px-8 py-3 border-2 border-neutral-200 rounded-xl cursor-pointer transition-all peer-checked:border-teal peer-checked:bg-teal/5">
                    <div className="font-semibold text-neutral-900">Yes</div>
                  </div>
                </label>
                
                <label className="relative">
                  <input
                    type="radio"
                    name="worksInHR"
                    value="no"
                    checked={formData.worksInHR === false}
                    onChange={() => handleInputChange('worksInHR', false)}
                    className="peer sr-only"
                  />
                  <div className="px-8 py-3 border-2 border-neutral-200 rounded-xl cursor-pointer transition-all peer-checked:border-teal peer-checked:bg-teal/5">
                    <div className="font-semibold text-neutral-900">No</div>
                  </div>
                </label>
              </div>
            </div>
          )}
          
          {/* Conditional fields for Sponsor */}
          {formData.userType === 'sponsor' && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  Your Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.sponsorRole}
                  onChange={(e) => handleInputChange('sponsorRole', e.target.value)}
                  className={`w-full px-4 py-3 border-2 ${errors.sponsorRole ? 'border-red-500' : 'border-neutral-200'} rounded-xl focus:outline-none focus:border-teal transition-colors`}
                >
                  <option value="">Select...</option>
                  <option value="owner">Owner/CEO</option>
                  <option value="cfo">CFO</option>
                  <option value="hr_manager">HR Manager</option>
                  <option value="benefits_admin">Benefits Administrator</option>
                  <option value="advisor">Financial Advisor</option>
                  <option value="other">Other</option>
                </select>
                {errors.sponsorRole && <p className="text-red-500 text-sm mt-1">{errors.sponsorRole}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  # of Employees <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.numEmployees}
                  onChange={(e) => handleInputChange('numEmployees', e.target.value)}
                  className={`w-full px-4 py-3 border-2 ${errors.numEmployees ? 'border-red-500' : 'border-neutral-200'} rounded-xl focus:outline-none focus:border-teal transition-colors`}
                >
                  <option value="">Select...</option>
                  <option value="1-10">1-10</option>
                  <option value="11-25">11-25</option>
                  <option value="26-50">26-50</option>
                  <option value="51-100">51-100</option>
                  <option value="101-250">101-250</option>
                  <option value="251-500">251-500</option>
                  <option value="501+">501+</option>
                </select>
                {errors.numEmployees && <p className="text-red-500 text-sm mt-1">{errors.numEmployees}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  Approx. Plan Assets
                </label>
                <select
                  value={formData.planAssets}
                  onChange={(e) => handleInputChange('planAssets', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-teal transition-colors"
                >
                  <option value="">Select...</option>
                  <option value="<250k">&lt; $250K</option>
                  <option value="250k-1m">$250K - $1M</option>
                  <option value="1m-5m">$1M - $5M</option>
                  <option value="5m-10m">$5M - $10M</option>
                  <option value="10m-25m">$10M - $25M</option>
                  <option value="25m+">$25M+</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Upload Plan Documents - ENHANCED */}
        <div className="bg-white border-2 border-neutral-200 rounded-2xl p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-neutral-900">Upload Plan Documents</h3>
              <p className="text-neutral-600">Help us better understand your plan (optional but recommended)</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Summary Plan Description */}
            <div className="border-2 border-neutral-200 rounded-xl p-6 hover:border-teal/30 transition-colors">
              <h4 className="font-semibold text-neutral-900 mb-2">Summary Plan Description (SPD)</h4>
              <p className="text-sm text-neutral-600 mb-4">
                Participant-facing document describing plan features
              </p>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => handleFileUpload(e, 'spd')}
                className="hidden"
                id="spd-upload"
                multiple
              />
              <label
                htmlFor="spd-upload"
                className="block w-full px-4 py-3 bg-teal/10 text-teal rounded-xl cursor-pointer hover:bg-teal/20 transition-colors text-center font-semibold"
              >
                📄 Upload SPD
              </label>
              
              {/* Show uploaded SPDs */}
              {uploadedDocuments.spd.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedDocuments.spd.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg">
                      <span className="text-sm text-neutral-700 truncate flex-1">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeDocument('spd', file.id)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Show extracted data preview */}
              {extractedData.spd && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-sm font-semibold text-green-800 mb-1">✓ Extracted Data:</div>
                  <div className="text-xs text-green-700 space-y-1">
                    <div>Plan: {extractedData.spd.plan_name}</div>
                    {extractedData.spd.has_safe_harbor_match && (
                      <div>Safe Harbor: {extractedData.spd.safe_harbor_formula}</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Basic Plan Document */}
            <div className="border-2 border-neutral-200 rounded-xl p-6 hover:border-teal/30 transition-colors">
              <h4 className="font-semibold text-neutral-900 mb-2">Basic Plan Document (BPD)</h4>
              <p className="text-sm text-neutral-600 mb-4">
                Prototype/template document defining plan structure
              </p>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => handleFileUpload(e, 'bpd')}
                className="hidden"
                id="bpd-upload"
                multiple
              />
              <label
                htmlFor="bpd-upload"
                className="block w-full px-4 py-3 bg-teal/10 text-teal rounded-xl cursor-pointer hover:bg-teal/20 transition-colors text-center font-semibold"
              >
                📘 Upload BPD
              </label>
              
              {uploadedDocuments.bpd.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedDocuments.bpd.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg">
                      <span className="text-sm text-neutral-700 truncate flex-1">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeDocument('bpd', file.id)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {extractedData.bpd && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-sm font-semibold text-green-800 mb-1">✓ Extracted Data:</div>
                  <div className="text-xs text-green-700 space-y-1">
                    <div>Provider: {extractedData.bpd.provider_name}</div>
                    <div>BPD #: {extractedData.bpd.document_number}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Adoption Agreement */}
            <div className="border-2 border-neutral-200 rounded-xl p-6 hover:border-teal/30 transition-colors">
              <h4 className="font-semibold text-neutral-900 mb-2">Adoption Agreement (AA)</h4>
              <p className="text-sm text-neutral-600 mb-4">
                Specific elections and plan provisions
              </p>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => handleFileUpload(e, 'adoption_agreement')}
                className="hidden"
                id="aa-upload"
                multiple
              />
              <label
                htmlFor="aa-upload"
                className="block w-full px-4 py-3 bg-teal/10 text-teal rounded-xl cursor-pointer hover:bg-teal/20 transition-colors text-center font-semibold"
              >
                📋 Upload AA
              </label>
              
              {uploadedDocuments.adoption_agreement.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedDocuments.adoption_agreement.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg">
                      <span className="text-sm text-neutral-700 truncate flex-1">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeDocument('adoption_agreement', file.id)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {extractedData.adoption_agreement && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-sm font-semibold text-green-800 mb-1">✓ Extracted Data:</div>
                  <div className="text-xs text-green-700 space-y-1">
                    <div>Plan: {extractedData.adoption_agreement.plan_name}</div>
                    {extractedData.adoption_agreement.match_enabled_flag && (
                      <div>Match: {extractedData.adoption_agreement.match_formula_text}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Extraction status */}
          {extracting && (
            <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <div className="flex items-center space-x-3">
                <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-blue-800 font-semibold">Extracting data from {currentExtracting}...</span>
              </div>
            </div>
          )}
        </div>

        {/* Section 4: Current Situation */}
        <div className="bg-white border-2 border-neutral-200 rounded-2xl p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-neutral-900">Current Situation</h3>
              <p className="text-neutral-600">Tell us about your current plan</p>
            </div>
          </div>
          
          <div className="space-y-6">
            {/* Current Provider */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Current Provider <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.currentProvider}
                onChange={(e) => handleInputChange('currentProvider', e.target.value)}
                className={`w-full px-4 py-3 border-2 ${errors.currentProvider ? 'border-red-500' : 'border-neutral-200'} rounded-xl focus:outline-none focus:border-teal transition-colors`}
              >
                <option value="">Select your current provider...</option>
                {currentProviders.map(provider => (
                  <option key={provider} value={provider}>{provider}</option>
                ))}
              </select>
              {errors.currentProvider && <p className="text-red-500 text-sm mt-1">{errors.currentProvider}</p>}
            </div>
            
            {/* Contract Renewal Date */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Contract Renewal Date (if known)
              </label>
              <input
                type="date"
                value={formData.renewalDate}
                onChange={(e) => handleInputChange('renewalDate', e.target.value)}
                className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-teal transition-colors"
              />
              <p className="text-sm text-neutral-500 mt-1">Helps us time our recommendations</p>
            </div>
            
            {/* Timeline */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-3">
                What's your timeline for making a change? <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {[
                  { value: 'immediate', label: 'Immediately (0-3 months)', description: 'Ready to switch now' },
                  { value: 'medium', label: 'Medium term (3-6 months)', description: 'Planning ahead' },
                  { value: 'long', label: 'Long term (6-12+ months)', description: 'Researching options' },
                  { value: 'researching', label: 'Just researching', description: 'Gathering information' },
                ].map(option => (
                  <label key={option.value} className="relative block">
                    <input
                      type="radio"
                      name="timeline"
                      value={option.value}
                      checked={formData.timeline === option.value}
                      onChange={(e) => handleInputChange('timeline', e.target.value)}
                      className="peer sr-only"
                    />
                    <div className="px-6 py-4 border-2 border-neutral-200 rounded-xl cursor-pointer transition-all peer-checked:border-teal peer-checked:bg-teal/5">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-semibold text-neutral-900">{option.label}</div>
                          <div className="text-sm text-neutral-600">{option.description}</div>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.timeline && <p className="text-red-500 text-sm mt-1">{errors.timeline}</p>}
            </div>
            
            {/* Pain Points */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-3">
                What are your main concerns? (check all that apply)
              </label>
              <div className="space-y-2">
                {painPointOptions.map(point => (
                  <label key={point.id} className="flex items-start space-x-3 p-3 hover:bg-neutral-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.painPoints.includes(point.id)}
                      onChange={() => handlePainPointToggle(point.id)}
                      className="w-5 h-5 text-teal border-2 border-neutral-300 rounded focus:ring-teal mt-0.5"
                    />
                    <span className="text-neutral-900">{point.label}</span>
                  </label>
                ))}
              </div>
              
              {formData.painPoints.includes('other') && (
                <input
                  type="text"
                  value={formData.otherPainPoint}
                  onChange={(e) => handleInputChange('otherPainPoint', e.target.value)}
                  className="w-full mt-3 px-4 py-3 border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-teal transition-colors"
                  placeholder="Please specify..."
                />
              )}
            </div>
          </div>
        </div>

        {/* Section 5: Get Provider Recommendations */}
        <div className="bg-white border-2 border-neutral-200 rounded-2xl p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-neutral-900">Get Provider Recommendations</h3>
              <p className="text-neutral-600">Receive detailed quotes from lower-cost providers</p>
            </div>
          </div>
          
          <p className="text-neutral-700 mb-6">
            Based on your analysis, we've identified 3 providers that could save you money while maintaining or improving service quality.
          </p>
          
          {/* Provider Options */}
          <div className="space-y-4 mb-6">
            <label className="flex items-start space-x-3 p-4 border-2 border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors">
              <input
                type="checkbox"
                checked={formData.selectedProviders.includes('all')}
                onChange={() => handleProviderToggle('all')}
                className="w-5 h-5 text-teal border-2 border-neutral-300 rounded focus:ring-teal mt-0.5"
              />
              <div className="flex-1">
                <div className="font-semibold text-neutral-900">All 3 recommended providers</div>
                <div className="text-sm text-neutral-600">Get comprehensive comparison (recommended)</div>
              </div>
            </label>
            
            {recommendedProviders.map(provider => (
              <label key={provider.id} className="flex items-start space-x-3 p-4 border-2 border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.selectedProviders.includes(provider.id)}
                  onChange={() => handleProviderToggle(provider.id)}
                  disabled={formData.selectedProviders.includes('all')}
                  className="w-5 h-5 text-teal border-2 border-neutral-300 rounded focus:ring-teal mt-0.5 disabled:opacity-50"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-neutral-900">{provider.name}</div>
                    <div className="text-sm font-semibold text-green-600">
                      Est. savings: ${provider.estimatedSavings.toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-neutral-600">Get detailed quote from {provider.name}</div>
                </div>
              </label>
            ))}
          </div>
          
          {/* Consultation */}
          <div className="p-4 bg-teal/5 border-2 border-teal/20 rounded-xl">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.wantsConsultation}
                onChange={(e) => handleCheckboxChange('wantsConsultation', e.target.checked)}
                className="w-5 h-5 text-teal border-2 border-teal/30 rounded focus:ring-teal mt-0.5"
              />
              <div>
                <div className="font-semibold text-neutral-900">Schedule a consultation call</div>
                <div className="text-sm text-neutral-600">Speak with a specialist about your options (15-30 min)</div>
              </div>
            </label>
          </div>
          
          {/* Contact Preferences */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-3">
                Preferred contact method:
              </label>
              <div className="space-y-2">
                {[
                  { value: 'email', label: 'Email only' },
                  { value: 'phone', label: 'Phone preferred' },
                  { value: 'either', label: 'Either is fine' },
                ].map(option => (
                  <label key={option.value} className="flex items-center space-x-3 p-3 border-2 border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors">
                    <input
                      type="radio"
                      name="preferredContact"
                      value={option.value}
                      checked={formData.preferredContact === option.value}
                      onChange={(e) => handleInputChange('preferredContact', e.target.value)}
                      className="w-4 h-4 text-teal border-neutral-300 focus:ring-teal"
                    />
                    <span className="text-neutral-900">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {formData.preferredContact !== 'email' && (
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-3">
                  Best time to call:
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'morning', label: 'Morning (8am-12pm)' },
                    { value: 'afternoon', label: 'Afternoon (12pm-5pm)' },
                    { value: 'evening', label: 'Evening (5pm-8pm)' },
                  ].map(option => (
                    <label key={option.value} className="flex items-center space-x-3 p-3 border-2 border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors">
                      <input
                        type="radio"
                        name="bestTimeToCall"
                        value={option.value}
                        checked={formData.bestTimeToCall === option.value}
                        onChange={(e) => handleInputChange('bestTimeToCall', e.target.value)}
                        className="w-4 h-4 text-teal border-neutral-300 focus:ring-teal"
                      />
                      <span className="text-neutral-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 6: Consent & Submit */}
        <div className="bg-white border-2 border-neutral-200 rounded-2xl p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-neutral-900">Consent & Submit</h3>
              <p className="text-neutral-600">Review and agree to continue</p>
            </div>
          </div>
          
          <div className="space-y-4 mb-6">
            <label className={`flex items-start space-x-3 p-4 border-2 ${errors.consentContact ? 'border-red-500 bg-red-50' : 'border-neutral-200'} rounded-xl cursor-pointer`}>
              <input
                type="checkbox"
                checked={formData.consentContact}
                onChange={(e) => handleCheckboxChange('consentContact', e.target.checked)}
                className="w-5 h-5 text-teal border-2 border-neutral-300 rounded focus:ring-teal mt-0.5"
              />
              <span className="text-neutral-900">
                I agree to be contacted about my 401(k) plan <span className="text-red-500">*</span>
              </span>
            </label>
            
            <label className={`flex items-start space-x-3 p-4 border-2 ${errors.consentSharing ? 'border-red-500 bg-red-50' : 'border-neutral-200'} rounded-xl cursor-pointer`}>
              <input
                type="checkbox"
                checked={formData.consentSharing}
                onChange={(e) => handleCheckboxChange('consentSharing', e.target.checked)}
                className="w-5 h-5 text-teal border-2 border-neutral-300 rounded focus:ring-teal mt-0.5"
              />
              <span className="text-neutral-900">
                I consent to sharing my information with recommended providers for quotes <span className="text-red-500">*</span>
              </span>
            </label>
            
            <label className={`flex items-start space-x-3 p-4 border-2 ${errors.consentTerms ? 'border-red-500 bg-red-50' : 'border-neutral-200'} rounded-xl cursor-pointer`}>
              <input
                type="checkbox"
                checked={formData.consentTerms}
                onChange={(e) => handleCheckboxChange('consentTerms', e.target.checked)}
                className="w-5 h-5 text-teal border-2 border-neutral-300 rounded focus:ring-teal mt-0.5"
              />
              <span className="text-neutral-900">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="text-teal hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" className="text-teal hover:underline">Privacy Policy</a>
                {' '}<span className="text-red-500">*</span>
              </span>
            </label>
            
            <label className={`flex items-start space-x-3 p-4 border-2 ${errors.consentDisclosure ? 'border-red-500 bg-red-50' : 'border-neutral-200'} rounded-xl cursor-pointer`}>
              <input
                type="checkbox"
                checked={formData.consentDisclosure}
                onChange={(e) => handleCheckboxChange('consentDisclosure', e.target.checked)}
                className="w-5 h-5 text-teal border-2 border-neutral-300 rounded focus:ring-teal mt-0.5"
              />
              <span className="text-neutral-900">
                I understand this is educational information, not fiduciary investment advice <span className="text-red-500">*</span>
              </span>
            </label>
          </div>
          
          {/* Additional Comments */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Additional comments or questions:
            </label>
            <textarea
              value={formData.additionalComments}
              onChange={(e) => handleInputChange('additionalComments', e.target.value)}
              rows="4"
              className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-teal transition-colors resize-none"
              placeholder="Tell us anything else we should know..."
            />
          </div>
          
          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-8 py-4 bg-teal text-white rounded-xl hover:bg-teal-dark transition-all font-bold text-lg shadow-soft disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                '🚀 Submit Request'
              )}
            </button>
          </div>
          
          <p className="text-sm text-neutral-500 text-center mt-4">
            By submitting this form, you'll receive your personalized report and provider recommendations within 2 business days.
          </p>
        </div>
      </form>
    </div>
  );
};

export default NextStepsTab;
